import { z } from 'zod/v4'

import {
  CANDLE_INTERVALS,
  INVENTORY_COVERAGE_SORT_KEYS,
} from '../../contracts/market-data/index.js'
import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

import { pageFields } from './page-fields.js'
import { adminPaginationSchema } from './users.screen.js'

/*
 * The three subtabs of the market data inventory, one screen each (their reads are not shared and the crypto coverage
 * takes 8.8 s, so only the subtab that opens by default is read cold): «Activos cripto» (`admin.marketData.inventory`),
 * «Divisas FX» (`…inventoryFx`) and «Conversiones» (`…inventoryConversions`). The rows are the ones the services build
 * today, field by field (`MarketDataInventoryStats`, `MarketDataAssetCoverage`, `FxInventoryStats`,
 * `FxCurrencyCoverage`, `FxMatrixResponse`); the type assertions of
 * `packages/backend/src/platform/server/__tests__/shared-market-data-inventory-screen.type-assertions.ts` tie them in
 * both directions.
 */

export const marketDataGapSchema = z.strictObject({
  from: z.string(),
  to: z.string(),
  missingDays: z.number(),
})

export const marketDataInventoryStatsSchema = z.strictObject({
  totalAssetsWithData: z.number(),
  totalCandleRecords: z.number(),
  totalFxRecords: z.number(),
  dateRangeStart: z.string().nullable(),
  dateRangeEnd: z.string().nullable(),
  sourcesCount: z.number(),
  sources: z.array(z.string()),
  assetsWithGaps: z.number(),
  intervalBreakdown: z.record(z.string(), z.number()),
})

export const marketDataAssetCoverageSchema = z.strictObject({
  assetId: z.string(),
  assetName: z.string(),
  assetSymbol: z.string(),
  assetType: z.string(),
  assetCategory: z.string().nullable(),
  source: z.string(),
  interval: z.enum(CANDLE_INTERVALS),
  firstDate: z.string(),
  lastDate: z.string(),
  recordCount: z.number(),
  expectedDays: z.number(),
  coveragePercent: z.number(),
  gaps: z.array(marketDataGapSchema),
  totalMissingDays: z.number(),
})

/**
 * The names are the ones of `GET …/inventory/coverage`. There is no `type` (the page sends none); `gapsOnly` travels as
 * text and the composition turns it into a boolean; `limit` is 25 because that is what the page asks for
 * (`inventory-page-size.ts`).
 */
export const adminMarketDataInventoryQuerySchema = z.strictObject({
  search: z.string().trim().min(1).max(100).optional(),
  source: z.string().trim().min(1).max(64).optional(),
  interval: z.enum(CANDLE_INTERVALS).optional(),
  gapsOnly: z.enum(['true', 'false']).optional(),
  sortBy: z.enum(INVENTORY_COVERAGE_SORT_KEYS).optional(),
  sortOrder: z.enum(['asc', 'desc']).default('asc'),
  ...pageFields(25),
})

export const adminMarketDataInventoryScreen = defineScreen({
  id: 'admin.marketData.inventory',
  path: '/api/screens/admin/market-data/inventory',
  access: 'super_admin',
  query: adminMarketDataInventoryQuerySchema,
  response: z.strictObject({
    stats: screenSection(marketDataInventoryStatsSchema),
    coverage: screenSection(
      z.strictObject({
        items: z.array(marketDataAssetCoverageSchema),
        pagination: adminPaginationSchema,
      }),
    ),
    downloadableSources: screenSection(z.array(z.string())),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.marketData'],
  // statements: 4 (stats) + 2 (coverage) + one per series with gaps of the page's assets (25 assets by default, each
  // with its source × interval series: a page full of gaps can pass this figure, which is documentary) + 1 (sources);
  // p95Ms: the coverage took 8.8 s on 2026-10-04.
  budget: { requests: 1, statements: 32, bytes: 60_000, p95Ms: 9_000 },
  summary:
    'Market data inventory, crypto assets: the figures, one page of coverage and the download sources',
})

export const fxInventoryStatsSchema = z.strictObject({
  totalCurrencies: z.number(),
  totalPairs: z.number(),
  totalRecords: z.number(),
  dateRangeStart: z.string().nullable(),
  dateRangeEnd: z.string().nullable(),
  currenciesWithGaps: z.number(),
})

export const fxCurrencyCoverageSchema = z.strictObject({
  currency: z.string(),
  pairCount: z.number(),
  totalRecords: z.number(),
  firstDate: z.string(),
  lastDate: z.string(),
  expectedDays: z.number(),
  coveragePercent: z.number(),
  gapCount: z.number(),
  totalMissingDays: z.number(),
})

/** The names are the ones of `GET …/inventory/fx/coverage`; 20 is what the subtab asks for (`FxCoverageTable.tsx`). */
export const adminMarketDataInventoryFxQuerySchema = z.strictObject({
  search: z.string().trim().min(1).max(100).optional(),
  gapsOnly: z.enum(['true', 'false']).optional(),
  ...pageFields(20),
})

export const adminMarketDataInventoryFxScreen = defineScreen({
  id: 'admin.marketData.inventoryFx',
  path: '/api/screens/admin/market-data/inventory/fx',
  access: 'super_admin',
  query: adminMarketDataInventoryFxQuerySchema,
  response: z.strictObject({
    stats: screenSection(fxInventoryStatsSchema),
    coverage: screenSection(
      z.strictObject({
        items: z.array(fxCurrencyCoverageSchema),
        pagination: adminPaginationSchema,
      }),
    ),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.marketData'],
  budget: { requests: 1, statements: 4, bytes: 20_000, p95Ms: 1_000 },
  summary: 'Market data inventory, FX currencies: the figures and one page of coverage',
})

export const fxMatrixSchema = z.strictObject({
  currencies: z.array(z.string()),
  matrix: z.record(z.string(), z.record(z.string(), z.number().nullable())),
  pairsWithGaps: z.array(
    z.strictObject({
      base: z.string(),
      quote: z.string(),
      gapCount: z.number(),
      totalMissingDays: z.number(),
    }),
  ),
})

export const adminMarketDataInventoryConversionsScreen = defineScreen({
  id: 'admin.marketData.inventoryConversions',
  path: '/api/screens/admin/market-data/inventory/conversions',
  access: 'super_admin',
  query: z.strictObject({}),
  response: z.strictObject({
    matrix: screenSection(fxMatrixSchema),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.marketData'],
  // statements: 1 (the matrix) + one gap read per pair WITH data: 56 with today's 8 `fxMajor` currencies (8 × 7).
  // Documentary: the list comes from `bat.assets` and, with none marked, is every active fiat currency.
  budget: { requests: 1, statements: 57, bytes: 20_000, p95Ms: 2_000 },
  summary: 'Market data inventory, conversions: the FX matrix and the pairs with gaps',
})

export type AdminMarketDataInventoryQuery = ScreenQuery<typeof adminMarketDataInventoryScreen>
export type AdminMarketDataInventoryData = ScreenData<typeof adminMarketDataInventoryScreen>
export type AdminMarketDataInventoryFxQuery = ScreenQuery<typeof adminMarketDataInventoryFxScreen>
export type AdminMarketDataInventoryFxData = ScreenData<typeof adminMarketDataInventoryFxScreen>
export type AdminMarketDataInventoryConversionsData = ScreenData<
  typeof adminMarketDataInventoryConversionsScreen
>
